import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/agencyProvider/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/agencyProvider/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/agencyProvider/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/agencyProvider/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/agencyProvider/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/agencyProvider/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/agencyProvider/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/agencyProvider/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/agencyProvider/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/agencyProvider/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/agencyProvider/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/agencyProvider/DELETE_FAILURE'

// Initial State
const initialState = {
  result: [],
  creating: false,
  deleting: false,
  loaded: false,
  loading: false,
  updating: false,
  errors: [],
}

// Actions
export function fetchRequest(){
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}){
  return {
    type: DELETE_SUCCESS,
    result: payload.result,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadAgencyProviders(options){
  return (dispatch) => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api('/admin/agency_providers.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

// Action Creators
export function createAgencyProvider(agencyProvider, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      agency_provider: agencyProvider,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/agencies/${agencyProvider.agency_id}/agency_providers.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add to result array
        const resultArray = getState().agencyProviders.result ? getState().agencyProviders.result.slice() : []
        if (!resultArray.includes(json.id)){
          resultArray.push(json.id)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess({ result: resultArray }))

        if (callback) callback()
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateAgencyProvider(agencyProvider, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      agency_provider: agencyProvider,
    }),
  }

  const { agency_id, id } = agencyProvider

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/agencies/${agency_id}/agency_providers/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteAgencyProvider(agencyProvider, options){
  const config = {
    method: 'DELETE',
    body: JSON.stringify({
      agency_provider: agencyProvider,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(deleteRequest())

    const { agency_id, id } = agencyProvider
    const promise = api(`/admin/agencies/${agency_id}/agency_providers/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Remove from result array
        const resultArray = getState().agencyProviders.result ? getState().agencyProviders.result.slice() : []
        const index = resultArray.indexOf(json.id)
        if (!resultArray.includes(json.id)){
          resultArray.splice(index, 1)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        result: action.result || state.result,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: action.errors,
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        result: action.result || state.result,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
